import { Spinner, useInitiallyHiddenElement } from '@tableau/tableau-ui';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../gen-src/Localize';
import { makeTestId } from '../utils/Declarative';
import { PropsAndElementAttrs, useTuaStatus } from '../utils/ReactHelpers';

export enum ActivitySpinnerSize {
  Large = 50,
  Small = 14
}

export interface ActivitySpinnerProps {
  /** Explanatory localized label (e.g. "Loading...") that should be presented by assistive technology, e.g. screen readers. */
  alt?: string;

  /** When true, a lighter color will be used for the spinner. */
  onDarkBackground?: boolean;

  /** When true, a semi transparent underlay will be shown. This is useful when showing the spinner on non-white backgrounds. */
  shouldShowUnderlay?: boolean;

  /**
   * * `ActivitySpinnerSize.Large` sets the spinner size to 50x50 px
   * * `ActivitySpinnerSize.Small` sets the spinner size to 14x14 px
   * @default ```ActivitySpinnerSize.Large```
   */
  spinnerSize?: ActivitySpinnerSize;

  /** Test id to be attached to the spinner. '-Spinner' will be automatically added as a suffix. */
  testId?: string;
}

/** An inline activity spinner */
export const ActivitySpinnerWidget = React.forwardRef<HTMLDivElement, PropsAndElementAttrs<ActivitySpinnerProps>>((props, ref) => {
  const { alt, onDarkBackground, shouldShowUnderlay, spinnerSize, testId, ...htmlAttrs } = props;
  const isTua = useTuaStatus();

  if (isTua) {
    return <TUASpinner {...props} ref={ref} />
  }

  return <Spinner
    alt={alt ?? Messages.waitIndicatorLabel()}
    data-tb-test-id={makeTestId(testId, 'Spinner')}
    {...htmlAttrs}
    color={onDarkBackground ? 'light' : 'dark'}
    dimension={spinnerSize}
    presentation={spinnerSize === ActivitySpinnerSize.Large ? 'alert' : 'inline'}
    ref={ref}
    showUnderlay={shouldShowUnderlay}
  />
});
ActivitySpinnerWidget.defaultProps = { spinnerSize: ActivitySpinnerSize.Large };
ActivitySpinnerWidget.displayName = 'ActivitySpinnerWidget';

const TUASpinnerStyleName = TypeStyle.style({
  $debugName: 'tuaSpinner',
  ['--slds-g-color-neutral-base-50' as string]: '#747474',
  ['--slds-g-color-neutral-base-100' as string]: '#FFFFFF',
  ['--spinner-dot-color' as string]: 'var(--slds-g-color-neutral-base-50)',
  ['--spinner-dimension' as string]: `${ActivitySpinnerSize.Large}px`,
  boxSizing: 'border-box',
  cursor: 'wait',
  height: `var(--spinner-dimension)`,
  width: `var(--spinner-dimension)`,
  $nest: {
    '&.inverse': {
      ['--spinner-dot-color' as string]: 'var(--slds-g-color-neutral-base-100)',
    },
    '&.small': {
      ['--spinner-dimension' as string]: `${ActivitySpinnerSize.Small}px`,
      ['--spinner-extra-transforms' as string]: 'translate(-11px, -11.5px) scale(0.25)', // centering magic numbers determined by visual inspection
    },
    '&.underlay': {
      background: 'rgba(255,255,255,.6)',
      borderRadius: '15%',
      padding: `calc(0.15 * var(--spinner-dimension))`,
      height: `calc(1.3 * var(--spinner-dimension))`,
      width: `calc(1.3 * var(--spinner-dimension))`,
    },
  }
});

const makeDotsAnimation = (distance: number) => TypeStyle.keyframes({
  $debugName: `dotAnimation`,
  '0%': { transform: `translate(0)` },
  '60%': { transform: `translate(0)`, animationTimingFunction: 'cubic-bezier(.55,.085,.68,.53)' },
  '80%': { transform: `translate(${distance}px, 0)`, animationTimingFunction: 'cubic-bezier(0,1.11,.7,1.43)' },
  '100%': { transform: `translate(0)` },
});

const SLDSSpinnerStyleName = TypeStyle.style({
  $debugName: 'sldsSpinner',
  boxSizing: 'border-box',
  display: 'inline-block',
  position: 'relative',
  height: 32,
  width: 32,
  transformOrigin: '50% 50%',
  transform: 'var(--spinner-extra-transforms,) translate(-7px, 9px) rotate(90deg)', // centering magic numbers determined by inspection, extra comma intentional
  $nest: {
    '&::before, &::after, &>::before, &>::after': {
      animationDuration: '1s',
      animationIterationCount: 'infinite',
      background: 'var(--spinner-dot-color)',
      borderRadius: '50%',
      content: `""`,
      display: 'inline-block',
      height: 8,
      position: 'absolute',
      transform: "translate(0)",
      width: 8,
      top: -4,
    },
    '&::before, &>::before': { animationName: makeDotsAnimation(-8), left: -4 },
    '&::after, &>::after': { animationName: makeDotsAnimation(8), right: -4 },
    '&>:first-child': { transform: 'rotate(60deg)', transformOrigin: '50% 50%' },
    '&>:last-child': { transform: 'rotate(120deg)', transformOrigin: '50% 50%' },
    '&::before': { animationDelay: 'calc(-1s / 12)' },
    '&>:first-child::before': { animationDelay: 'calc(1s / 12)' },
    '&>:last-child::before': { animationDelay: 'calc(3s / 12)' },
    '&::after': { animationDelay: 'calc(5s / 12)' },
    '&>:first-child::after': { animationDelay: 'calc(7s / 12)' },
    '&>:last-child::after': { animationDelay: 'calc(9s / 12)' },
  }
});

/** reproduction of https://www.lightningdesignsystem.com/components/spinners/#About-Spinners */
const TUASpinner = React.forwardRef<HTMLDivElement, PropsAndElementAttrs<ActivitySpinnerProps>>(
  ({ alt, onDarkBackground, shouldShowUnderlay, spinnerSize, testId, ...htmlAttrs }, ref) => {
    return (
      <div
        data-tb-test-id={makeTestId(testId, 'Spinner')}
        role='status'
        {...htmlAttrs}
        className={TypeStyle.classes(
          TUASpinnerStyleName,
          { inverse: onDarkBackground, small: spinnerSize === ActivitySpinnerSize.Small, underlay: shouldShowUnderlay },
          htmlAttrs.className,
        )}
        ref={ref}
      >
        <div
          aria-hidden={useInitiallyHiddenElement() && spinnerSize === ActivitySpinnerSize.Large}
          aria-label={alt ?? Messages.waitIndicatorLabel()}
          className={SLDSSpinnerStyleName}
          role='img'
        >
          <div /><div />
        </div>
      </div>
    );
  }
);
TUASpinner.displayName = 'TUASpinner';
